<script setup lang="ts">
import VButton from '~/components/VButton.vue';
import VInput from '~/components/VInput.vue';
import { emailRegex } from '~/constants/regex';
import { useAuthStore } from '~/stores/AuthStore';
import AuthHeader from './AuthHeader.vue';
import PasswordInput from './PasswordInput.vue';

const authStore = useAuthStore();
const loading = ref(false);
const privacyPolicyAccepted = ref(false);

const isValidEmail = computed(() => emailRegex.test(authStore.email));

async function handleRegister() {
  loading.value = true;
  await authStore.register();
  loading.value = false;
  if (authStore.token) authStore.authError = '';
  if (!authStore.authError) authStore.handleIsOpened(false);
}

function clearForm() {
  authStore.authError = '';
  authStore.name = '';
  authStore.email = '';
  authStore.password = '';
  privacyPolicyAccepted.value = false;
}

const isRegisterAvailable = computed(
  () => !!authStore.name && !!authStore.email && !!authStore.password && !loading.value && isValidEmail && !!privacyPolicyAccepted.value
);

const country = useState<string | undefined>('country', () => undefined);

const isRU = computed(() => country.value === 'RU');

const privacyPolicyLink = computed(() => (isRU.value ? '/legal/ru/privacy-policy.pdf' : '/legal/en/privacy-policy.pdf'));

const publicOfferLink = computed(() => (isRU.value ? '/legal/ru/public-offer.pdf' : '/legal/en/public-offer.pdf'));
</script>

<template>
  <form
    class="auth"
    @keydown.enter.prevent="handleRegister"
    @submit.prevent="handleRegister"
  >
    <AuthHeader title="Регистрация аккаунта" />

    <div class="auth__actions">
      <v-input
        v-model="authStore.name"
        required
        type="text"
        autocomplete="name"
        autofocus
        placeholder="Имя"
        aria-label="Имя"
      />

      <v-input
        v-model="authStore.email"
        required
        type="email"
        autocomplete="new-email"
        placeholder="Почта"
        aria-label="Email"
      />

      <PasswordInput
        v-model="authStore.password"
        required
        autocomplete="new-password"
        aria-label="Пароль"
      />

      <div class="privacy-policy">
        <input
          id="privacy-policy"
          v-model="privacyPolicyAccepted"
          type="checkbox"
          required
          aria-required="true"
        />
        <label
          for="privacy-policy"
          style="hyphens: auto"
        >
          Я ознакомился с <a :href="privacyPolicyLink">политикой конфиденциальности</a> и принимаю условия
          <a :href="publicOfferLink">публичной оферты</a>.
        </label>
      </div>

      <div
        v-if="authStore.authError"
        class="error mb-3"
        role="alert"
      >
        {{ authStore.authError }}
      </div>

      <span
        v-if="authStore.email && authStore.password && !isValidEmail"
        class="register-error"
        role="alert"
      >
        Почта указана неверно
      </span>

      <v-button
        medium
        type="submit"
        :disabled="!isRegisterAvailable"
        :aria-busy="loading"
        style="width: 100%"
      >
        Зарегистрироваться
      </v-button>
    </div>

    <div class="auth__footer">
      Уже есть аккаунт?
      <v-button
        schema="text"
        type="button"
        @click="
          authStore.handleIsOpened('login');
          clearForm();
        "
      >
        Войти
      </v-button>
    </div>
  </form>
</template>

<style scoped lang="scss">
.auth {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;

  .register-error {
    width: 100%;
    text-align: start;
    color: $text-status-destructive;
  }

  .auth__actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;

    .new-button {
      width: 100%;
    }
  }

  .privacy-policy {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 8px 0;

    input[type='checkbox'] {
      margin-top: 3px;
      cursor: pointer;
      width: 15px !important;
      height: 15px !important;
      flex-shrink: 0;
    }

    label {
      font-size: 14px;
      line-height: 18px;
      color: $foreground-gray;
    }
  }

  .auth__agreement {
    text-align: center;
    color: $foreground-gray;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
  }

  .auth__footer {
    font-size: 14px;
    line-height: 20px;
    color: #9691f0;
  }

  .error {
    color: $text-status-destructive;
  }
}
</style>
